import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { Dialog, DialogContent, InputBase, List, ListItem, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import { AddCircleOutline, Search as SearchIcon, Receipt, People, Inventory, Store } from '@mui/icons-material';
import { QuickAddCustomer, QuickAddVendor, QuickAddInventoryItem } from './QuickAddModals';
import './CommandPalette.css';

/**
 * CommandPalette — Quick action and navigation overlay
 * Triggered via Ctrl+K / Cmd+K
 * Gated by 'commandPalette' feature flag
 */
const CommandPalette = () => {
  const isEnabled = useFeatureFlag('commandPalette', false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [quickAddModal, setQuickAddModal] = useState(null); // 'customer', 'vendor', 'item'
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // The global shortcut listener
  useEffect(() => {
    if (!isEnabled) return;

    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEnabled]);

  // Reset state when opening/closing
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Timeout ensures the input is rendered before focusing
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Command definitions
  const commands = [
    { id: 'add-customer', title: 'New Customer', icon: <People />, type: 'action', action: () => setQuickAddModal('customer') },
    { id: 'add-vendor', title: 'New Vendor', icon: <Store />, type: 'action', action: () => setQuickAddModal('vendor') },
    { id: 'add-item', title: 'New Inventory Item', icon: <Inventory />, type: 'action', action: () => setQuickAddModal('item') },
    { id: 'nav-invoices', title: 'Go to Invoices', icon: <Receipt />, type: 'nav', action: () => navigate('/sales/invoices') },
    { id: 'nav-customers', title: 'Go to Customers', icon: <People />, type: 'nav', action: () => navigate('/crm/customers') },
    { id: 'nav-inventory', title: 'Go to Inventory', icon: <Inventory />, type: 'nav', action: () => navigate('/inventory/items') }
  ];

  // Filter based on query
  const filteredCommands = commands.filter(cmd => 
    cmd.title.toLowerCase().includes(query.toLowerCase())
  );

  // Keyboard navigation within the palette
  const handleDialogKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredCommands.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCommands.length > 0) {
        handleExecute(filteredCommands[selectedIndex]);
      }
    }
  };

  const handleExecute = (cmd) => {
    setOpen(false);
    if (cmd.action) {
      cmd.action();
    }
  };

  if (!isEnabled) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: 'command-palette-paper'
        }}
      >
        <div className="command-palette-header" onKeyDown={handleDialogKeyDown}>
          <SearchIcon className="command-palette-search-icon" />
          <InputBase
            inputRef={inputRef}
            placeholder="Type a command or search (e.g. 'New Customer')"
            className="command-palette-input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            fullWidth
          />
        </div>
        <DialogContent className="command-palette-content">
          {filteredCommands.length === 0 ? (
            <Box className="command-palette-empty">
              <Typography color="textSecondary">No results found for "{query}"</Typography>
            </Box>
          ) : (
            <List className="command-palette-list">
              {filteredCommands.map((cmd, index) => (
                <ListItem
                  button
                  key={cmd.id}
                  className={`command-palette-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleExecute(cmd)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <ListItemIcon className="command-palette-item-icon">
                    {cmd.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={cmd.title} 
                    secondary={cmd.type === 'action' ? 'Quick Action' : 'Navigation'} 
                  />
                  {index === selectedIndex && (
                    <Typography variant="caption" className="command-palette-hint">
                      ↵ to select
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Add Modals triggered from Palette */}
      <QuickAddCustomer 
        open={quickAddModal === 'customer'} 
        onClose={() => setQuickAddModal(null)}
        onSuccess={() => {}} 
      />
      <QuickAddVendor 
        open={quickAddModal === 'vendor'} 
        onClose={() => setQuickAddModal(null)}
        onSuccess={() => {}} 
      />
      {/* Assuming QuickAddInventoryItem exists as per standard structure */}
    </>
  );
};

export default CommandPalette;
